package or.garden.manager.features.calendar

import androidx.compose.runtime.*
import or.garden.manager.domain.model.CropData
import or.garden.manager.features.catalog.components.CropDetailsView
import or.garden.manager.ui.utils.getCropImageSource
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.dom.*

@Composable
fun CalendarScreen(
    crops: List<CropData>,
    onCropClick: (CropData) -> Unit
) {
    var selectedCropForDetails by remember { mutableStateOf<CropData?>(null) }

    if (selectedCropForDetails != null) {
        CropDetailsView(
            crop = selectedCropForDetails!!,
            onBack = { selectedCropForDetails = null },
            onToggleFavorite = { 

            },
            isTogglingFavorite = false
        )
    } else {
        CalendarTableView(
            crops = crops,
            onCropClick = { selectedCropForDetails = it }
        )
    }
}

@Composable
private fun CalendarTableView(crops: List<CropData>, onCropClick: (CropData) -> Unit) {
    val months = listOf("Jan", "Feb", "Mar", "Apr", "Maj", "Jun", "Jul", "Avg", "Sep", "Okt", "Nov", "Dec")

    Div({
        classes("bg-white", "rounded-3xl", "shadow-sm", "border", "border-gray-100", "overflow-hidden")
    }) {
        // Header with Legend
        Div({ classes("p-8", "bg-emerald-50/50", "border-b", "border-gray-100") }) {
            Div({
                classes("flex", "flex-col", "md:flex-row", "justify-between", "items-start", "md:items-center", "gap-4")
            }) {
                H2({
                    classes("text-2xl", "font-bold", "text-emerald-900")
                }) { Text("Kalendar sadnje i berbe") }

                Div({ classes("flex", "gap-6") }) {
                    LegendItem("Sadnja", "bg-emerald-500/30", "border-emerald-500/50")
                    LegendItem("Berba", "bg-blue-500/30", "border-blue-500/50")
                    LegendItem(
                        "Sadnja i Berba",
                        "bg-gradient-to-r", "from-emerald-500/30", "to-blue-500/30",
                        "border-gray-300"
                    )
                }
            }
        }

        // Table Container
        Div({ classes("overflow-x-auto") }) {
            Div({
                classes("min-w-[1000px]")
                style {
                    property("display", "grid")
                    property("grid-template-columns", "240px repeat(12, 1fr)")
                }
            }) {
                // Header Row
                Div({
                    classes("p-4", "font-bold", "text-gray-400", "uppercase", "text-xs", "tracking-wider", "bg-gray-50")
                }) { Text("Kultura") }
                months.forEach { month ->
                    Div({
                        classes(
                            "p-4",
                            "font-bold",
                            "text-gray-400",
                            "uppercase",
                            "text-xs",
                            "tracking-wider",
                            "bg-gray-50",
                            "text-center",
                            "border-l",
                            "border-gray-100"
                        )
                    }) { Text(month) }
                }

                // Data Rows
                crops.filter { it.id != -1 }.forEach { crop ->
                    // Crop Name Cell
                    Div({
                        classes(
                            "p-4",
                            "flex",
                            "items-center",
                            "gap-3",
                            "border-t",
                            "border-gray-100",
                            "hover:bg-gray-50",
                            "cursor-pointer",
                            "transition-colors"
                        )
                        onClick { onCropClick(crop) }
                    }) {
                        Div({
                            classes(
                                "w-8",
                                "h-8",
                                "bg-emerald-50",
                                "rounded-lg",
                                "flex",
                                "items-center",
                                "justify-center",
                                "flex-shrink-0"
                            )
                        }) {
                            val icon = getCropImageSource(crop.icon)
                            if (icon != null) Img(src = icon, attrs = { classes("w-5", "h-5", "object-contain") })
                            else I({ classes("fas", "fa-leaf", "text-emerald-600", "text-xs") })
                        }
                        Span({
                            classes("font-bold", "text-gray-700", "truncate")
                        }) { Text(crop.name) }
                    }

                    // Month Cells
                    for (monthIdx in 1..12) {
                        val info = getMonthInfo(crop, monthIdx)
                        Div({
                            classes("p-0", "border-t", "border-l", "border-gray-100", "relative")
                            style { position(Position.Relative) }
                        }) {
                            if (info.isPlanting && info.isHarvest) {
                                // Horizontal split: Top half planting, Bottom half harvest
                                Div({
                                    style {
                                        position(Position.Absolute)
                                        property("top", "0"); property("left", "0"); property("right", "0"); property("height", "50%")
                                        backgroundColor(Color("rgba(16, 185, 129, 0.3)")) // Emerald
                                    }
                                })
                                Div({
                                    style {
                                        position(Position.Absolute)
                                        property("bottom", "0"); property("left", "0"); property("right", "0"); property("height", "50%")
                                        backgroundColor(Color("rgba(59, 130, 246, 0.3)")) // Blue
                                    }
                                })
                            } else if (info.isPlanting) {
                                Div({ classes("w-full", "h-full", "bg-emerald-500/30") })
                            } else if (info.isHarvest) {
                                Div({ classes("w-full", "h-full", "bg-blue-500/30") })
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun LegendItem(label: String, vararg classes: String) {
    Div({ classes("flex", "items-center", "gap-2") }) {
        Div({ classes("w-4", "h-4", "rounded", "border", *classes) })
        Span({ classes("text-sm", "font-medium", "text-gray-600") }) { Text(label) }
    }
}

private data class MonthInfo(
    val isPlanting: Boolean = false,
    val isHarvest: Boolean = false
)

private fun getMonthInfo(crop: CropData, monthNumber: Int): MonthInfo {
    val plantingRanges = mutableListOf<String>()

    crop.transplantSchedule?.let { schedule ->
        listOfNotNull(
            schedule.continental,
            schedule.mediterranean,
            schedule.mountain
        ).forEach { window ->
            if (window.startMonth != null && window.endMonth != null) {
                plantingRanges.add(
                    "${monthNumberToName(window.startMonth!!)}-${monthNumberToName(window.endMonth!!)}"
                )
            }
        }
    }

    val isPlanting = plantingRanges.any { isMonthInRange(monthNumber, it) }

    val harvestMonths = mutableSetOf<Int>()
    plantingRanges.forEach { range ->
        val months = range.split("-")
        if (months.size == 2) {
            val startMonth = monthNameToNumber(months[0])
            val endMonth = monthNameToNumber(months[1])
            if (startMonth != null && endMonth != null) {
                val harvestStart = ((startMonth + 2) % 12).let { if (it == 0) 12 else it }
                val harvestEnd = ((endMonth + 4) % 12).let { if (it == 0) 12 else it }

                if (harvestStart <= harvestEnd) {
                    (harvestStart..harvestEnd).forEach { harvestMonths.add(it) }
                } else {
                    ((harvestStart..12) + (1..harvestEnd)).forEach { harvestMonths.add(it) }
                }
            }
        }
    }

    return MonthInfo(
        isPlanting = isPlanting,
        isHarvest = monthNumber in harvestMonths
    )
}

private fun isMonthInRange(monthNumber: Int, range: String): Boolean {
    val months = range.split("-")
    if (months.size == 2) {
        val startMonth = monthNameToNumber(months[0])
        val endMonth = monthNameToNumber(months[1])
        if (startMonth != null && endMonth != null) {
            return if (startMonth <= endMonth) {
                monthNumber in startMonth..endMonth
            } else {
                monthNumber >= startMonth || monthNumber <= endMonth
            }
        }
    }
    return false
}

private fun monthNameToNumber(monthName: String): Int? = when (monthName.trim().lowercase()) {
    "jan" -> 1; "feb" -> 2; "mar" -> 3; "apr" -> 4; "may", "maj" -> 5; "jun" -> 6
    "jul" -> 7; "aug", "avg" -> 8; "sep" -> 9; "oct", "okt" -> 10; "nov" -> 11; "dec" -> 12
    else -> null
}

private fun monthNumberToName(monthNumber: Int): String? = when (monthNumber) {
    1 -> "Jan"; 2 -> "Feb"; 3 -> "Mar"; 4 -> "Apr"; 5 -> "May"; 6 -> "Jun"
    7 -> "Jul"; 8 -> "Aug"; 9 -> "Sep"; 10 -> "Oct"; 11 -> "Nov"; 12 -> "Dec"
    else -> null
}
